import {
  FC,
  useState,
  useCallback,
  useMemo,
  ReactNode,
  SyntheticEvent,
} from 'react'
import Image, { ImageProps } from 'next/image'

import { getSafeHtml } from '../../utils/sanitizeUtils'
import { colors } from '../../theme'
import { ActionButtonProps } from '../ActionButton'
import BottomPanel from '../BottomPanel'
import Collapsable from '../Collapsable'
import Highlight, { HighlightProps } from '../Highlight'
import { ECollapsable } from '../Collapsable/types'
import { TagAgeProps } from '../Family/TagAge'

import * as SC from './styled'

export type NutritionalValue = {
  name: string
  quantity?: string
  includingName?: string
  includingQuantity?: string
}

export type RecipeIntroductionSectionProps = {
  type?: string
  switchButtonProps?: ActionButtonProps
  favoriteButtonProps?: ActionButtonProps | null
  shareButtonProps?: ActionButtonProps
  preparationText?: string
  bakingText?: string
  restText?: string
  nutriscore?: string
  nutritionalValuesText?: string
  aboutRecipeTitleText?: string
  aboutRecipeText?: ReactNode
  imageProps?: ImageProps & { fallbacksrc?: string }
  nutritionalValues?: NutritionalValue[]
  energeticValueText?: string
  energeticValueKcal?: string
  energeticValueKj?: string
  isCourseSide?: boolean
  onSelectSwap?: (side: string) => void
  swapCourseText?: string
  swapSideText?: string
  selectedSide?: string
  onNutritionalClick?: (ev: SyntheticEvent) => void
  highlightTitleProps?: HighlightProps
  tagAgeProps?: TagAgeProps
}

const RecipeIntroductionSection: FC<RecipeIntroductionSectionProps> = ({
  type,
  switchButtonProps,
  favoriteButtonProps,
  preparationText,
  bakingText,
  restText,
  nutriscore,
  nutritionalValuesText,
  aboutRecipeTitleText,
  aboutRecipeText,
  imageProps,
  nutritionalValues,
  energeticValueText,
  energeticValueKcal,
  energeticValueKj,
  isCourseSide,
  onSelectSwap,
  swapCourseText,
  swapSideText,
  selectedSide,
  onNutritionalClick,
  shareButtonProps,
  highlightTitleProps,
  tagAgeProps,
}) => {
  const [
    isNutritionalValuesOpen,
    setIsNutritionalValuesOpen,
  ] = useState<boolean>(false)
  const [imgSrc, imgSrcSet] = useState(imageProps?.src)

  const onNutrionalValuesClickHandler = useCallback(
    (ev) => {
      onNutritionalClick?.(ev)
      setIsNutritionalValuesOpen((prev) => !prev)
    },
    [onNutritionalClick]
  )

  const renderList = useMemo(() => {
    return (
      nutritionalValues?.map((nv: NutritionalValue, index) => (
        <SC.ListItem key={`nv-${index}`} role="listitem">
          <SC.Line>
            <dt>{nv.name}</dt>
            <dd>{nv.quantity && <span>{nv.quantity}</span>}</dd>
          </SC.Line>
          {nv.includingName && (
            <SC.Line className="secondLine">
              <dt>{nv.includingName}</dt>
              <dd>
                {nv.includingQuantity && <span>{nv.includingQuantity}</span>}
              </dd>
            </SC.Line>
          )}
        </SC.ListItem>
      )) ?? null
    )
  }, [nutritionalValues])

  return (
    <>
      <BottomPanel
        heading={
          <SC.NutritionalValuesPanelHeading>
            <SC.IconPlateLove>
              <Image
                src="/images/RecipeIntroductionSection/magnifying-glass.png"
                height={125}
                width={125}
              />
            </SC.IconPlateLove>
            <SC.PanelTitle>{nutritionalValuesText}</SC.PanelTitle>
          </SC.NutritionalValuesPanelHeading>
        }
        isOpen={isNutritionalValuesOpen}
        toggleBottomPanelFunction={onNutrionalValuesClickHandler}
      >
        <SC.NutritionalValuesPanelContent>
          <SC.EnergeticValue>
            <dt>{energeticValueText}</dt>
            <dd>{energeticValueKcal}</dd>
            <dt></dt>
            <dd>{energeticValueKj}</dd>
          </SC.EnergeticValue>
          <SC.List role="list">{renderList}</SC.List>
        </SC.NutritionalValuesPanelContent>
      </BottomPanel>
      {isCourseSide && (
        <SC.SwitchCourseContainer>
          <SC.SwitchCourseButton
            isActive={selectedSide === 'course'}
            onClick={() => onSelectSwap?.('course')}
          >
            <SC.SwitchButtonTitle>
              {swapCourseText && (
                <Highlight
                  backgroundColor={colors.pink}
                  color={colors.pureBlack}
                  text={swapCourseText}
                />
              )}
            </SC.SwitchButtonTitle>
          </SC.SwitchCourseButton>
          <SC.SwitchCourseButton
            isActive={selectedSide === 'side'}
            onClick={() => onSelectSwap?.('side')}
          >
            <SC.SwitchButtonTitle>
              {swapSideText && (
                <Highlight
                  backgroundColor={colors.pink}
                  color={colors.pureBlack}
                  text={swapSideText}
                />
              )}
            </SC.SwitchButtonTitle>
          </SC.SwitchCourseButton>
        </SC.SwitchCourseContainer>
      )}
      <Collapsable
        rounded={!isCourseSide}
        header={
          <SC.HeaderContainer>
            <SC.IntroductionHeader>
              {type && !isCourseSide && (
                <SC.SectionTitle>
                  <Highlight
                    backgroundColor={
                      highlightTitleProps?.backgroundColor || colors.pink
                    }
                    color={highlightTitleProps?.color || colors.pureBlack}
                    text={type}
                  />
                  {tagAgeProps && <SC.TagAge {...tagAgeProps} />}
                </SC.SectionTitle>
              )}
              <SC.Buttons isSwitchCourse={isCourseSide}>
                {switchButtonProps && (
                  <SC.SwitchButton {...switchButtonProps} />
                )}
                {shareButtonProps && <SC.ShareButton {...shareButtonProps} />}
                {favoriteButtonProps && (
                  <SC.FavoriteButton {...favoriteButtonProps} />
                )}
              </SC.Buttons>
            </SC.IntroductionHeader>
          </SC.HeaderContainer>
        }
        isCollapsable={ECollapsable.NONE}
        isOpenOnInit
      >
        <SC.Inner>
          <SC.LeftSection>
            {preparationText && (
              <SC.DurationInfo>
                <SC.DurationIcon icon="knife" />
                <SC.DurationText
                  dangerouslySetInnerHTML={getSafeHtml(preparationText)}
                />
              </SC.DurationInfo>
            )}
            {bakingText && (
              <SC.DurationInfo>
                <SC.DurationIcon icon="cookingPan" />
                <SC.DurationText
                  dangerouslySetInnerHTML={getSafeHtml(bakingText)}
                />
              </SC.DurationInfo>
            )}
            {restText && (
              <SC.DurationInfo>
                <SC.DurationIcon icon="restCooking" />
                <SC.DurationText
                  dangerouslySetInnerHTML={getSafeHtml(restText)}
                />
              </SC.DurationInfo>
            )}
            <SC.Nutrition>
              {nutriscore && (
                <SC.NutriscoreImg>
                  <Image
                    src={`/images/nutriscore/nutriscore-${nutriscore.toLowerCase()}.png`}
                    layout="fill"
                  />
                </SC.NutriscoreImg>
              )}
              {nutritionalValuesText && (
                <SC.NutritionalValues
                  onClickHandler={onNutrionalValuesClickHandler}
                >
                  {nutritionalValuesText}
                </SC.NutritionalValues>
              )}
            </SC.Nutrition>
            {aboutRecipeText && (
              <SC.About>
                {aboutRecipeTitleText && (
                  <SC.AboutTitle>
                    <SC.AboutIcon icon="lightbulb" />
                    <span>{aboutRecipeTitleText}</span>
                  </SC.AboutTitle>
                )}
                {aboutRecipeText && (
                  <SC.AboutContent>{aboutRecipeText}</SC.AboutContent>
                )}
              </SC.About>
            )}
          </SC.LeftSection>

          <SC.RightSection>
            {imageProps && imgSrc && (
              <>
                <SC.RecipeImageMobile>
                  <Image
                    {...imageProps}
                    src={imgSrc}
                    onError={() => {
                      imgSrcSet(imageProps?.fallbacksrc)
                    }}
                  />
                </SC.RecipeImageMobile>
                <SC.RecipeImageDesktop>
                  <Image
                    {...imageProps}
                    src={imgSrc}
                    onError={() => {
                      imgSrcSet(imageProps?.fallbacksrc)
                    }}
                  />
                </SC.RecipeImageDesktop>
              </>
            )}
          </SC.RightSection>
        </SC.Inner>
      </Collapsable>
    </>
  )
}

export default RecipeIntroductionSection
